import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  Close
} from "@mui/icons-material";

import { ModelBase } from "@models";
import { 
  Character,
  GameClass,
  CharacterFeatureSource,
  CharacterClass,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectIndex from "@dndComponents/select/SelectObjectIndex";
import CharacterFeatureSourcesOuter from "@dndComponents/character/features/CharacterFeatureSourcesOuter";
import AddButton from "@components/input/buttons/AddButton";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterClassInput({
  char: char,
}: Props) {
  const [new_class, setNewClass] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [filter, setFilter] = useState<any>({ sidekick: char.sidekick });
  const [expanded_class_id, setExpandedClassID] = useState(char.classes.length === 1 ? char.classes[0].true_id : "");
  // const [swapping, setSwapping] = useState<GameClass | null>(null);
  const [change_time, setChangeTime] = useState(0);
  const [class_level, setClassLevel] = useState(char.classes.length > 0 ? char.classes[0].level : 0);
  const [processing, setProcessing] = useState(false);

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // const recalculated = useCallback(() => {
  //   setTimeout(() => {
  //     if (processing) {
  //       setNewClass(false);
  //       setChangeTime(common_util.get_time_number());
  //       setProcessing(false);
  //     }
  //   }, 100);
  // }, [processing]);

  const recalculated = useCallback(() => {
    console.log("Char Classes - Recalculated");
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      if (processing) {
        setProcessing(false);
        setNewClass(false);
        setChangeTime(common_util.get_time_number());
      }
    }
  }, [reloading, processing, char]);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [recalculated]);

  const updateFilter = () => {
    const new_filter: any = {};
    
    const search_string = api.search_string;
    if (search_string !== "") {
      // new_filter.search_string = search_string;
      // new_filter["or"] = [{ "name*includes": search_string },{ "description*includes": search_string }];
      new_filter["search_string"] = search_string;
    }
    const source_book = api.get_filter("source_book");
    if (Object.keys(source_book).length > 0) {
      new_filter.source_book_ids = source_book;
    }

    setFilter(new_filter);
  }

  const getClassDisabledReason = (gc: GameClass) => {
    // if (gc.name.includes("Fighter")) {
    //   debugger;
    // }
    let reason = "";
    if (gc.sidekick !== char.sidekick) {
      if (gc.sidekick) {
        reason = "This class is only for Sidekicks.";
      } else {
        reason = "This class is not for Sidekicks.";
      }
    } else if (gc.sidekick && char.sidekick) {
      console.log(gc);
      if (char.sidekick_ci && char.sidekick_creature) {
        // Check the limitations on the class against the creature
        reason = char_util.fitLimits(char, gc.limitations,"game_class", gc._id);
        if (reason === "true") {
          reason = "";
        }
      } else {
        reason = "First choose a Creature to become a Sidekick.";
      }
    } else {
      const primary_abilities = gc.primary_abilities;
      let found = primary_abilities.length === 0;
      let i = 0;
      while (i < primary_abilities.length && !found) {
        const score = char.current_ability_scores.getAbilityScore(primary_abilities[i]);
        if (score && score >= 13) {
          found = true;
          break;
        }
        ++i;
      }
      if (!found) {
        reason = `Prerequisites not met: ${primary_abilities.join(" or ")} 13`;
      } else {
        const secondary_abilities = gc.secondary_abilities;
        found = secondary_abilities.length === 0;
        i = 0;
        while (i < secondary_abilities.length && !found) {
          const score = char.current_ability_scores.getAbilityScore(secondary_abilities[i]);
          if (score && score >= 13) {
            found = true;
            break;
          }
          ++i;
        }
        if (!found) {
          reason = `Prerequisites not met: ${secondary_abilities.join(" or ")} 13`;
        }
      }
      if (reason !== "") {
        reason += ` from ${gc.name}\n`;
      }
    }
    return reason;
  }

  const renderAddAnotherClass = () => {
    if (!char.sidekick && (char.classes.length === 0 || char.character_level >= 1) && char.character_options.allow_multiclassing) {
      return (
        <div style={{ float: "right" }}>
          <AddButton
            label="Add Another Class"
            onClick={() => {
              setNewClass(true);
              setExpandedClassID("");
            }} 
          />
        </div>
      );
    }
  }

  if (char.classes.length === 0 || new_class) {
    return (
      <Grid container spacing={0} direction="row">
        { new_class && 
          <Grid size={12}>
            <Button variant="contained" color="primary"
              onClick={() => {
                setNewClass(false);
                if (char.classes.length === 1) {
                  setExpandedClassID(char.classes[0].true_id);
                  setClassLevel(char.classes[0].level);
                }
              }}>
              Cancel Add
            </Button>
          </Grid>
        }
        { processing ?
          <Grid size={12} className="header centered">Adding Class</Grid>
        :
          <Grid size={12}>
            <SelectObjectIndex
              data_type="game_class"
              character={char}
              filter={filter}
              updateFilter={updateFilter}
              log_it
              onClick={async (obj: ModelBase) => {
                // debugger;
                if (obj instanceof GameClass) {
                  setProcessing(true);
                  const char_class = await connect_util.addClass(char, obj);
                  setExpandedClassID(char_class.true_id);
                  setClassLevel(char_class.level);
                }
              }}
              disableObj={(obj: ModelBase) => {
                let disabled = false;
                let reasons = "";
                if (processing) {
                  disabled = true;
                } else if (char.sidekick || (char.classes.length > 0 && !char.character_options.ignore_multiclassing_rules)) {
                  if (obj instanceof GameClass) {
                    if (char.classes.filter(o => 
                      o.source_id === obj._id || 
                      o.variant_ids.includes(obj._id)).length > 0) {
                      disabled = true;
                      reasons = "You already have this class";
                    } else {
                      // Need to check all the char_classes' primary and secondary abilities
                      // As well as the game_class's
                      // I should also check these if the ability_scores are being modified
                      for (const char_class of char.classes) {
                        if (char_class.source) {
                          const reason = getClassDisabledReason(char_class.source);
                          if (reason.length > 0) {
                            reasons += reason;
                            // reasons += ` from ${char_class.name}\n`;
                            disabled = true;
                          }
                        }
                      }
                      const reason2 = getClassDisabledReason(obj);
                      if (reason2.length > 0) {
                        reasons += reason2;
                        // reasons += ` from ${obj.name}\n`;
                        disabled = true;
                      }
                    }
                  }
                }
                return { disabled, reasons };
              }}
            />
          </Grid>
        }
      </Grid>
    );
  } else {
    console.log("rendering on character class input");
    const positions = char.classes.sort((a, b) => (a.position > b.position ? 1 : -1)).map(o => `${o.position + 1}`);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          <Grid container spacing={0} direction="row">
            <Grid
              className="header"
              size={12}>
              Character Level { char.character_level }
            </Grid>
            <Grid size={12}>
              Max Hit Points: { char.hp.max }
            </Grid>
            <Grid size={12}>
              Hit Dice: { char.hit_dice_string() }
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          { renderAddAnotherClass() }
        </Grid>
        <Grid size={12}>
          <CharacterFeatureSourcesOuter
            char={char}
            sources={char.classes}
            change_time={change_time}
            expanded_cfs_id={expanded_class_id}
            show_cfb_levels
            // log_it
            extra_cfs_header_control={(cfs: CharacterFeatureSource) => {
              if (cfs instanceof CharacterClass) {
                const char_class = cfs;
                const other_class_levels = char.character_level - char_class.level;
                const class_levels: string[] = [];
                for (let i = 1; i <= (20 - other_class_levels); ++i) {
                  class_levels.push(`${i}`);
                }
                return (
                  <Grid container spacing={0} direction="row">
                    <Grid size={9}>
                      <SelectStringBox 
                        name="Level"
                        options={class_levels}
                        value={`${class_level}`}
                        onChange={(changed: string) => {
                          const level = +changed;
                          setClassLevel(level);
                          setTimeout(() => {
                            char_util.changeSourceLevel(char, char_class.source_id, level);
                            char_util.repopulateCharFeatureBases(char).then(() => {
                              setChangeTime(common_util.get_time_number());
                            });
                          }, 1000);
                        }}
                      />
                    </Grid>
                    <Grid size={3}>
                      <Close style={{ cursor: "pointer" }}
                        onClick={() => {
                          connect_util.removeClass(char, char_class);
                          setNewClass(false);
                          setExpandedClassID("");
                          if (char.classes.length === 1) {
                            setClassLevel(char.classes[0].level);
                          }
                          setChangeTime(common_util.get_time_number());
                        }} 
                      />
                    </Grid>
                    { positions.length > 1 &&
                      <Grid size={12}>
                        <SelectStringBox 
                          name="Position"
                          options={positions}
                          value={`${char_class.position + 1}`}
                          onChange={(changed: string) => {
                            setProcessing(true);
                            const old_position = char_class.position;
                            const new_position: number = (+changed) - 1;
                            if (new_position < old_position) {
                              let position = old_position - 1;
                              while (position >= new_position) {
                                const old_pos = position;
                                const cc = char.classes.find(o => o.position === old_pos);
                                if (cc) {
                                  cc.position++;
                                }
                                position--;
                              }
                            } else {
                              let position = old_position + 1;
                              while (position <= new_position) {
                                const old_pos = position;
                                const cc = char.classes.find(o => o.position === old_pos);
                                if (cc) {
                                  cc.position--;
                                }
                                position++;
                              }
                            }
                            char_class.position = new_position;
                            char.changed();
                            char.subscription_holder.recalculate();
                          }}
                        />
                      </Grid>
                    }
                  </Grid>
                );
              }
              return undefined;
            }}
            cfs_sort={(a: CharacterFeatureSource, b: CharacterFeatureSource) => {
              if (a instanceof CharacterClass && b instanceof CharacterClass) {
                return a.position > b.position ? 1 : -1;
              }
              return 1;
            }}
            onCFSSelect={(cfs: CharacterFeatureSource | undefined) => {
              setExpandedClassID(cfs ? cfs.true_id : "");
              if (cfs instanceof CharacterClass) {
                setClassLevel(cfs.level);
              }
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
